import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams } from 'react-router-dom';
import './App.css';

// Contexte admin
import { AdminProvider, useAdmin } from './contexts/AdminContext';

// Composants
import Header from './components/Header';
import Calendar from './components/Calendar';
import DailyView from './components/DailyView';
import BookingForm from './components/BookingForm';
import Dashboard from './components/Dashboard';
import AdminLogin from './components/AdminLogin';
import { PaymentSuccess, PaymentCancel } from './components/PaymentResult';
import BookingStatus from './components/BookingStatus';
import WeeklyPlanning from './components/WeeklyPlanning';
import { GiftCardPurchase, GiftCardView } from './components/GiftCard';
import { ReferralPage } from './components/Referral';
import TournamentManager from './components/TournamentManager';
import Footer from './components/Footer';

// Services
import { 
  getSessions, getBookings, 
  createBooking, deleteBooking, updateBookingPayment, updateBookingNotes,
  blockSession, unblockSession, createSession, 
  openSlot, closeSlot, openAllSlotsForDay, closeAllSlotsForDay,
  getContacts, deleteContact,
  getGiftCards, markGiftCardUsed, deleteGiftCard,
  recalculateBookingSlotOccupancy,
  getBookingSettings, setFieldMode,
  initializeData 
} from './services/dataService';

// Config
import { isDayOpen, isInSeason, ALL_SLOTS } from './config/formulas';

function App() {
  return (
    <AdminProvider>
      <Router>
        <AppContent />
      </Router>
    </AdminProvider>
  );
}

function AppContent() {
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  // Mode terrain : lève la restriction du délai d'1h pour tous les clients
  // (activé par l'admin quand il est physiquement sur place)
  const [fieldMode, setFieldModeState] = useState(false);
  const [fieldModeUntil, setFieldModeUntil] = useState(null);
  const { isAdmin } = useAdmin();

  // Initialisation
  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeData();
        setInitialized(true);
      } catch (err) {
        console.error("Erreur lors de l'initialisation:", err);
        setError("Erreur lors de l'initialisation des données");
      }
    };
    initialize();
  }, []);

  // Chargement des données
  useEffect(() => {
    const loadData = async () => {
      if (!initialized) return;
      try {
        setLoading(true);
        const [sessionsData, bookingsData, contactsData, giftCardsData, settingsData] = await Promise.all([
          getSessions(),
          getBookings(),
          getContacts(),
          getGiftCards(),
          getBookingSettings()
        ]);
        setSessions(sessionsData);
        setBookings(bookingsData);
        setContacts(contactsData);
        setGiftCards(giftCardsData);
        setFieldModeState(settingsData.fieldMode);
        setFieldModeUntil(settingsData.fieldModeUntil);
      } catch (err) {
        setError('Erreur lors du chargement des données: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [initialized]);

  // Recharger les données
  const reloadData = async () => {
    try {
      const [sessionsData, bookingsData, contactsData, giftCardsData, settingsData] = await Promise.all([
        getSessions(),
        getBookings(),
        getContacts(),
        getGiftCards(),
        getBookingSettings()
      ]);
      setSessions(sessionsData);
      setBookings(bookingsData);
      setContacts(contactsData);
      setGiftCards(giftCardsData);
      setFieldModeState(settingsData.fieldMode);
      setFieldModeUntil(settingsData.fieldModeUntil);
    } catch (err) {
      console.error("Erreur lors du rechargement:", err);
    }
  };

  // Vérification périodique du mode terrain.
  // Indispensable côté client : quand Nicolas active le mode depuis son
  // téléphone, les visiteurs déjà sur le site doivent le voir sans avoir
  // à recharger la page. Assure aussi l'extinction automatique à minuit.
  useEffect(() => {
    if (!initialized) return;

    const verifierModeTerrain = async () => {
      try {
        const settingsData = await getBookingSettings();
        setFieldModeState(settingsData.fieldMode);
        setFieldModeUntil(settingsData.fieldModeUntil);
      } catch (err) {
        console.error("Erreur vérification mode terrain:", err);
      }
    };

    const intervalle = setInterval(verifierModeTerrain, 60000); // toutes les 60 s
    return () => clearInterval(intervalle);
  }, [initialized]);

  // Activer / désactiver le mode terrain (admin uniquement)
  const handleToggleFieldMode = async (enabled) => {
    try {
      const result = await setFieldMode(enabled);
      setFieldModeState(result.fieldMode);
      setFieldModeUntil(result.fieldModeUntil);
      return true;
    } catch (err) {
      setError('Erreur mode terrain: ' + err.message);
      return false;
    }
  };

  // ====== HANDLERS ======
  
  const handleDateChange = (date) => setCurrentDate(date);

  const handleCreateBooking = async (bookingData) => {
    try {
      const newBooking = await createBooking(bookingData);
      await reloadData();
      return newBooking; // Retourner l'objet complet (avec id) pour Stripe
    } catch (err) {
      setError('Erreur: ' + err.message);
      return null;
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await deleteBooking(bookingId);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur: ' + err.message);
      return false;
    }
  };

  const handleUpdateBookingPayment = async (bookingId, isPaid, paymentInfo = null) => {
    try {
      await updateBookingPayment(bookingId, isPaid, paymentInfo);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur: ' + err.message);
      return false;
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteContact(contactId);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur suppression contact: ' + err.message);
      return false;
    }
  };

  const handleUpdateBookingNotes = async (bookingId, notes) => {
    try {
      await updateBookingNotes(bookingId, notes);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur notes: ' + err.message);
      return false;
    }
  };

  // Recalculer l'occupation du créneau d'une réservation
  // (utile pour corriger les anciennes résas créées avant la logique de privatisation)
  const handleRecalculateOccupancy = async (bookingId) => {
    try {
      const result = await recalculateBookingSlotOccupancy(bookingId);
      await reloadData();
      return result;
    } catch (err) {
      setError('Erreur recalcul: ' + err.message);
      throw err;
    }
  };

  const handleMarkGiftCardUsed = async (code, giftCardId) => {
    try {
      await markGiftCardUsed(code, null);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur carte cadeau: ' + err.message);
      return false;
    }
  };

  const handleDeleteGiftCard = async (giftCardId) => {
    try {
      await deleteGiftCard(giftCardId);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur suppression carte cadeau: ' + err.message);
      return false;
    }
  };

  const handleCreateSession = async (sessionData) => {
    try {
      const newSession = await createSession(sessionData);
      await reloadData();
      return newSession;
    } catch (err) {
      setError('Erreur: ' + err.message);
      return null;
    }
  };

  const handleBlockSession = async (sessionId, reason) => {
    try {
      await blockSession(sessionId, reason);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur: ' + err.message);
      return false;
    }
  };

  const handleUnblockSession = async (sessionId) => {
    try {
      await unblockSession(sessionId);
      await reloadData();
      return true;
    } catch (err) {
      setError('Erreur: ' + err.message);
      return false;
    }
  };

  const handleOpenSlot = async (sessionId) => {
    try { await openSlot(sessionId); await reloadData(); return true; }
    catch (err) { setError('Erreur: ' + err.message); return false; }
  };

  const handleCloseSlot = async (sessionId) => {
    try { await closeSlot(sessionId); await reloadData(); return true; }
    catch (err) { setError('Erreur: ' + err.message); return false; }
  };

  const handleOpenDay = async (dateStr) => {
    try { await openAllSlotsForDay(dateStr); await reloadData(); return true; }
    catch (err) { setError('Erreur: ' + err.message); return false; }
  };

  const handleCloseDay = async (dateStr) => {
    try { await closeAllSlotsForDay(dateStr); await reloadData(); return true; }
    catch (err) { setError('Erreur: ' + err.message); return false; }
  };

  if (loading) {
    return <div className="loading">Chargement des données...</div>;
  }

  return (
    <div className="App">
      <Header isAdmin={isAdmin} />
      
      {error && (
        <div className="error-bar">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-dismiss">✕</button>
        </div>
      )}
      
      <main className="content">
        <Routes>
          {/* ====== ROUTES PUBLIQUES ====== */}
          
          {/* Calendrier */}
          <Route path="/" element={
            <Calendar 
              currentDate={currentDate}
              sessions={sessions}
              bookings={bookings}
              onDateChange={handleDateChange}
              isInSeason={isInSeason}
              isDayOpen={isDayOpen}
              isAdmin={isAdmin}
            />
          } />
          
          {/* Vue journalière (admin voit les boutons de gestion, public non) */}
          <Route path="/daily/:dateParam" element={
            <DailyView 
              currentDate={currentDate}
              sessions={sessions}
              bookings={bookings}
              isAdmin={isAdmin}
              onDeleteBooking={handleDeleteBooking}
              onUpdateBookingPayment={handleUpdateBookingPayment}
              onUpdateBookingNotes={handleUpdateBookingNotes}
              onBlockSession={handleBlockSession}
              onUnblockSession={handleUnblockSession}
              onCreateSession={handleCreateSession}
              onOpenSlot={handleOpenSlot}
              onCloseSlot={handleCloseSlot}
              onOpenDay={handleOpenDay}
              onCloseDay={handleCloseDay}
              onRecalculateOccupancy={handleRecalculateOccupancy}
              fieldMode={fieldMode}
              fieldModeUntil={fieldModeUntil}
              onToggleFieldMode={handleToggleFieldMode}
            />
          } />
          
          {/* Formulaire de réservation (public) */}
          <Route path="/booking/:dateParam" element={
            <BookingFormWrapper
              sessions={sessions}
              onCreateBooking={handleCreateBooking}
              fieldMode={fieldMode}
            />
          } />

          {/* ====== ROUTES PAIEMENT ====== */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* ====== PAGE STATUT RÉSERVATION (client) ====== */}
          <Route path="/booking/status/:bookingId" element={<BookingStatus />} />

          {/* ====== VUE PLANNING SEMAINE (admin) ====== */}
          <Route path="/planning" element={
            <WeeklyPlanning
              sessions={sessions}
              bookings={bookings}
              currentDate={currentDate}
            />
          } />

          {/* ====== CARTES CADEAUX ====== */}
          <Route path="/giftcard" element={<GiftCardPurchase />} />
          <Route path="/giftcard/:giftCode" element={<GiftCardView />} />

          {/* ====== PARRAINAGE ====== */}
          <Route path="/referral/:referralCode" element={<ReferralPage />} />

          {/* ====== TOURNOIS LASER TAG (offline) ====== */}
          <Route path="/tournois" element={<TournamentManager />} />

          {/* ====== ROUTES ADMIN ====== */}
          
          {/* Login admin */}
          <Route path="/admin" element={<AdminLogin />} />
          
          {/* Dashboard admin */}
          <Route path="/admin/dashboard" element={
            <Dashboard
              sessions={sessions}
              bookings={bookings}
              contacts={contacts}
              giftCards={giftCards}
              onUpdateBookingPayment={handleUpdateBookingPayment}
              onDeleteContact={handleDeleteContact}
              onMarkGiftCardUsed={handleMarkGiftCardUsed}
              onDeleteGiftCard={handleDeleteGiftCard}
            />
          } />
        </Routes>
      </main>
      
      <Footer />
    </div>
  );
}

// Wrapper pour BookingForm
function BookingFormWrapper({ sessions, onCreateBooking, fieldMode }) {
  const { dateParam } = useParams();
  return (
    <BookingForm
      date={dateParam}
      sessions={sessions}
      onCreateBooking={onCreateBooking}
      fieldMode={fieldMode}
    />
  );
}

export default App;
